import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'

import { ApiErrorCode } from '@/api/apiTypes'
import { BaseView, OTPInput, ResendCountdown } from '@/components'
import {
  useCheckCodeSentToEmail,
  useCheckCodeSentToPhone,
  useSendCodeToPhone,
  useSignInWithEmail,
  VerifyCodeDTO,
} from '@/domain'
import { useResetNavigation } from '@/hooks'
import { useAuthScopedStore } from '@/store/scoped'
import { messagingMethods } from '@/constants'
import { responsiveValueAdapter } from '@/utils'

const CELL_COUNT = 4
const defaultCodes = Array(CELL_COUNT).fill('')

export const SignUpEnterVerificationCodeForm = () => {
  const [codes, setCodes] = useState<string[]>(defaultCodes)
  const [inputErrorMessage, setInputErrorMessage] = useState('')

  const { t } = useTranslation()
  const navigate = useNavigate()
  const resetTo = useResetNavigation()

  const email = useAuthScopedStore((state) => state.email)
  const phone = useAuthScopedStore((state) => state.phone)
  const messagingMethod = useAuthScopedStore((state) => state.messagingMethod)

  const identificationMethod = useAuthScopedStore(
    (state) => state.identificationMethod,
  )
  const signUpForm = useAuthScopedStore((state) => state.signUpForm)
  const setSignUpForm = useAuthScopedStore((state) => state.setSignUpForm)

  const userId = signUpForm.userId
  const isEmailIdentificationMethod = identificationMethod === 'email'

  const { mutateAsync: resendPhoneCode, isPending: isSendingPhoneCode } =
    useSendCodeToPhone({
      onError: ({ errorMessage }) => {
        if (errorMessage) {
          toast.error(errorMessage)
        }
      },
    })

  const { mutateAsync: resendEmailCode, isPending: isSendingEmailCode } =
    useSignInWithEmail({
      onSuccess: ({ user_id }) => {
        // Update userId in case it changed
        setSignUpForm({ userId: user_id })
        toast.success(t('screens:signup.codeResent'))
      },
      onError: ({ errorMessage }) => {
        if (errorMessage) {
          toast.error(errorMessage)
        }
      },
    })

  const onSuccess = useCallback(
    async (data: VerifyCodeDTO) => {
      const { next_step, verify_token } = data

      // Сохраняем verify_token для следующих шагов
      if (verify_token) {
        setSignUpForm({ verifyToken: verify_token })
      }

      if (next_step === 'SETUP_PASSWORD') {
        navigate('/auth/signup/create-password')
      } else if (next_step === 'SETUP_PROFILE') {
        navigate('/setup-profile')
      } else if (next_step === 'VERIFY_PASSWORD') {
        navigate('/auth/enter-password')
      } else {
        // Если неизвестный следующий шаг, переходим на главную
        resetTo('dashboard')
      }
    },
    [navigate, resetTo, setSignUpForm],
  )

  const onError = useCallback(
    ({
      code,
      errorMessage,
    }: {
      code?: ApiErrorCode
      errorMessage?: string
    }) => {
      if (!errorMessage) return

      if (code === ApiErrorCode.CODE_INVALID) {
        setInputErrorMessage(errorMessage)
        return
      }

      toast.error(errorMessage)
    },
    [],
  )

  const {
    mutateAsync: checkCodeSentToEmail,
    isPending: isCheckingCodeSentToEmail,
  } = useCheckCodeSentToEmail({
    onSuccess,
    onError,
  })

  const {
    mutateAsync: checkCodeSentToPhone,
    isPending: isCheckingCodeSentToPhone,
  } = useCheckCodeSentToPhone({
    onSuccess,
    onError: (error) => {
      onError({
        errorMessage: error.errorMessage,
        code: error.code as ApiErrorCode,
      })
    },
  })

  const handleCodeVerification = useCallback(
    async (fullCode: string) => {
      const isLoading = isCheckingCodeSentToEmail || isCheckingCodeSentToPhone
      if (isLoading) return

      setInputErrorMessage('') // Очищаем ошибки

      if (isEmailIdentificationMethod) {
        await checkCodeSentToEmail({
          email: email || '',
          user_id: userId || '',
          code: fullCode,
        })
      } else {
        // Phone verification
        await checkCodeSentToPhone({
          user_id: userId || '',
          phone_number: phone?.number
            ? `${phone.countryCode}${phone.number}`
            : '',
          code: fullCode,
        })
      }
    },
    [
      isCheckingCodeSentToEmail,
      isCheckingCodeSentToPhone,
      isEmailIdentificationMethod,
      checkCodeSentToEmail,
      checkCodeSentToPhone,
      userId,
      email,
      phone,
    ],
  )

  const handleResendCode = useCallback(() => {
    const isLoading = isSendingPhoneCode || isSendingEmailCode
    if (isLoading) return

    if (isEmailIdentificationMethod) {
      if (!email) return
      resendEmailCode({ email })
    } else {
      if (!phone?.number || !messagingMethod) return
      resendPhoneCode({
        phone_number: phone?.number
          ? `${phone.countryCode}${phone.number}`
          : '',
        send_method: messagingMethod,
      })
    }
  }, [
    isSendingPhoneCode,
    isSendingEmailCode,
    isEmailIdentificationMethod,
    email,
    phone?.number,
    messagingMethod,
    resendEmailCode,
    resendPhoneCode,
  ])

  return (
    <>
      <OTPInput
        autoFocus
        autoFocusAfterClean
        numberCells={4}
        cellStyle={{
          fontSize: 32,
          fontWeight: 600,
        }}
        keyboardType="numeric"
        textContentType="one-time-code"
        variant="filled"
        codes={codes}
        isSubmitting={isCheckingCodeSentToEmail || isCheckingCodeSentToPhone}
        onSubmit={handleCodeVerification}
        setCodes={setCodes}
        errorMessage={inputErrorMessage}
        setErrorMessage={setInputErrorMessage}
      />
      <BaseView gap={28} center>
        {/* Messaging method icon/image could be added here */}
        <BaseView className="w-10 h-10">
          {identificationMethod === 'phone' && (
            <img
              src={
                messagingMethods.methods.find(
                  (method) => method.code === messagingMethod,
                )?.img_url
              }
              alt={
                messagingMethods.methods.find(
                  (method) => method.code === messagingMethod,
                )?.name
              }
              style={{
                width: responsiveValueAdapter(40, true),
                height: responsiveValueAdapter(40, true),
                display: identificationMethod === 'phone' ? 'block' : 'none',
              }}
            />
          )}
        </BaseView>
        <ResendCountdown
          onPress={handleResendCode}
          displayCountdown={true}
          label={t('screens:signup.resend')}
          fontFamily="bold"
          countDownOnLabel={t('screens:signup.resend')}
          labelDisabled="label.disabled"
          labelClickable="label.default"
        />
      </BaseView>
    </>
  )
}
